/**
 * 工作流列表页面：直接展示所有公开工作流，底部追加图生文分析工具
 */

import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Image as ImageIcon, Loader2, Sparkles, Tag, Zap } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import { usePublicWorkflows } from '@/hooks'
import type { PublicWorkflow } from '@/types/workflow'
import { cn } from '@/lib/utils'
import type { ImageToTextToolConfig } from '@/data/imageToTextTools'
import { AI_ANALYSIS_TOOL } from '@/data/imageToTextTools'

/** 全量拉取时每页数量 */
const FETCH_ALL_PAGE_SIZE = 500 as const

/** 每页展示数量 */
const PAGE_SIZE = 20 as const

/**
 * 图生文静态工具卡片组件
 */
function ToolCard({ tool }: { tool: ImageToTextToolConfig }): ReactElement {
  return (
    <Link to={tool.href} className="block h-full">
      <Card className="group h-full gap-0 overflow-hidden border-border/70 py-0 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10">
        {/* 封面区（固定色块 + emoji） */}
        <div className="relative flex aspect-[142/89] items-center justify-center overflow-hidden bg-muted">
          <span className="text-5xl transition-transform duration-300 group-hover:scale-110">
            {tool.icon}
          </span>
          <span className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/40 text-white/90 backdrop-blur-sm">
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </div>

        <CardHeader className="space-y-1 p-3 pb-0.5">
          <CardTitle className="line-clamp-1 text-sm font-semibold leading-5">{tool.title}</CardTitle>
          <CardDescription className="line-clamp-2 min-h-8 break-words text-xs leading-4 text-muted-foreground">
            {tool.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="p-3 pt-1">
          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary/70" />
            <span>一键分析</span>
          </span>
        </CardContent>
      </Card>
    </Link>
  )
}

// ─────────────────────────────────────────────────────────────
// ImageToTextModuleView（图生文模块专用视图）
// ─────────────────────────────────────────────────────────────

/**
 * 工作流卡片组件属性
 */
interface WorkflowCardProps {
  workflow: PublicWorkflow
}

/**
 * 工作流卡片组件
 */
function WorkflowCard({ workflow }: WorkflowCardProps): ReactElement {
  const primaryTag = workflow.tags[0]
  const secondaryTag = workflow.tags[1]
  const workflowDescription =
    workflow.description !== '' ? workflow.description : '暂无描述，点击查看详细用法。'
  const normalizedDescription = workflowDescription.replace(/\s+/g, ' ').trim()
  const hasSecondaryTag = secondaryTag !== undefined && secondaryTag !== ''

  return (
    <Link to={`/workflows/${workflow.id}`} className="block h-full">
      <Card className="group h-full gap-0 overflow-hidden border-border/70 py-0 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10">
        {/* 封面图 */}
        <div className="relative aspect-[142/89] overflow-hidden bg-muted">
          {workflow.cover_url !== '' ? (
            <img
              src={workflow.cover_url}
              alt={workflow.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <ImageIcon className="h-9 w-9 text-muted-foreground/50" />
            </div>
          )}

          {primaryTag !== undefined ? (
            <div className="absolute left-2 top-2 flex max-w-[calc(100%-3rem)] flex-wrap gap-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-xs font-medium text-primary-foreground">
                <Sparkles className="h-3 w-3" />
                <span className="line-clamp-1">{primaryTag}</span>
              </span>
            </div>
          ) : null}

          <span className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/40 text-white/90 backdrop-blur-sm">
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </div>

        <CardHeader className="space-y-1 p-3 pb-0.5">
          <CardTitle className="line-clamp-1 text-sm font-semibold leading-5">{workflow.title}</CardTitle>
          <CardDescription className="line-clamp-2 min-h-8 break-words text-xs leading-4 text-muted-foreground">
            {normalizedDescription}
          </CardDescription>
        </CardHeader>

        <CardContent className="p-3 pt-1">
          <div className={cn('flex items-center gap-2 text-xs', hasSecondaryTag ? 'justify-between' : 'justify-start')}>
            <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
              <Zap className="h-3.5 w-3.5 text-amber-500" />
              <span>消耗 {workflow.power_consumption} 算力</span>
            </span>

            {hasSecondaryTag ? (
              <span className="inline-flex max-w-[45%] items-center gap-1 rounded-md border border-border/70 bg-background/60 px-2 py-1 text-[11px] text-muted-foreground">
                <Tag className="h-3.5 w-3.5" />
                <span className="line-clamp-1">{secondaryTag}</span>
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

// ─────────────────────────────────────────────────────────────
// SubCategoryFilter
// ─────────────────────────────────────────────────────────────

interface SubCategoryFilterProps {
  categories: Array<{ id: string; name: string }>
  selected: string | null
  onSelect: (id: string | null) => void
}

/**
 * 子分类筛选 Chips：基于当前模块工作流的 category 动态渲染
 */
function SubCategoryFilter({ categories, selected, onSelect }: SubCategoryFilterProps): ReactElement | null {
  if (categories.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={(): void => { onSelect(null) }}
        className={cn(
          'inline-flex items-center rounded-md border px-3 py-1 text-xs font-medium transition-all',
          selected === null
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
        )}
      >
        全部
      </button>
      {categories.map((cat) => (
        <button
          key={cat.id}
          type="button"
          onClick={(): void => { onSelect(cat.id) }}
          className={cn(
            'inline-flex items-center rounded-md border px-3 py-1 text-xs font-medium transition-all',
            selected === cat.id
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
          )}
        >
          {cat.name}
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// generatePageNumbers（分页辅助）
// ─────────────────────────────────────────────────────────────

function generatePageNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages: number[] = []
  const showEllipsisStart = currentPage > 4
  const showEllipsisEnd = currentPage < totalPages - 3

  pages.push(1)
  if (showEllipsisStart) pages.push(-1)

  const start = showEllipsisStart ? Math.max(2, currentPage - 1) : 2
  const end = showEllipsisEnd ? Math.min(totalPages - 1, currentPage + 1) : totalPages - 1
  for (let i = start; i <= end; i++) pages.push(i)

  if (showEllipsisEnd) pages.push(-1)
  if (totalPages > 1) pages.push(totalPages)

  return pages
}

// ─────────────────────────────────────────────────────────────
// WorkflowsPage（主页面）
// ─────────────────────────────────────────────────────────────

/**
 * 工作流列表页面：直接展示所有工作流 + 底部图生文分析工具，无 Tab 切换
 */
export function WorkflowsPage(): ReactElement {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  const {
    data: workflowResult,
    isLoading: workflowsLoading,
    error: workflowsError,
  } = usePublicWorkflows({
    page: 1,
    pageSize: FETCH_ALL_PAGE_SIZE,
  })

  const allWorkflows = workflowResult?.list ?? []

  // 从所有工作流中提取去重后的子分类
  const categories = useMemo(() => {
    const seen = new Set<string>()
    const result: Array<{ id: string; name: string }> = []
    for (const w of allWorkflows) {
      if (w.category !== null && w.category !== undefined && !seen.has(w.category.id)) {
        seen.add(w.category.id)
        result.push({ id: w.category.id, name: w.category.name })
      }
    }
    return result
  }, [allWorkflows])

  // 按子分类过滤
  const filteredWorkflows = useMemo(
    () =>
      selectedCategory === null
        ? allWorkflows
        : allWorkflows.filter((w) => w.category?.id === selectedCategory),
    [allWorkflows, selectedCategory]
  )

  const totalPages = Math.max(1, Math.ceil(filteredWorkflows.length / PAGE_SIZE))
  const pagedWorkflows = filteredWorkflows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  function handleCategoryChange(id: string | null): void {
    setSelectedCategory(id)
    setCurrentPage(1)
  }

  function handlePageChange(page: number): void {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">AI 创作工具</h1>
        <p className="text-muted-foreground">选择一个工作流开始创作，不同工作流会消耗不同算力</p>
      </div>

      {/* 加载中 */}
      {workflowsLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">加载中...</span>
        </div>
      ) : workflowsError !== null ? (
        <div className="rounded-xl border bg-card p-10 text-center">
          <p className="text-sm text-destructive">加载失败: {workflowsError.message}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* 子分类筛选 */}
          <SubCategoryFilter
            categories={categories}
            selected={selectedCategory}
            onSelect={handleCategoryChange}
          />

          {/* 工作流网格 */}
          {pagedWorkflows.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {pagedWorkflows.map((workflow) => (
                <WorkflowCard key={workflow.id} workflow={workflow} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border bg-card p-10 text-center">
              <p className="text-sm text-muted-foreground">
                {allWorkflows.length === 0 ? '暂无工作流应用' : '该分类下暂无工作流'}
              </p>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 ? (
            <Pagination className="mt-2">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(): void => { handlePageChange(currentPage - 1) }}
                    className={cn(currentPage <= 1 && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>

                {generatePageNumbers(currentPage, totalPages).map((pageNum, index) =>
                  pageNum === -1 ? (
                    <PaginationItem key={`ellipsis-${String(index)}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        isActive={pageNum === currentPage}
                        onClick={(): void => { handlePageChange(pageNum) }}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={(): void => { handlePageChange(currentPage + 1) }}
                    className={cn(currentPage >= totalPages && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          ) : null}

          {/* 统计信息 */}
          {filteredWorkflows.length > 0 ? (
            <div className="text-center text-sm text-muted-foreground">
              共 {filteredWorkflows.length} 个工作流
              {totalPages > 1 ? `，当前第 ${String(currentPage)} / ${String(totalPages)} 页` : null}
            </div>
          ) : null}

          {/* AI 分析工具分区 */}
          <section className="space-y-3 border-t pt-6">
            <div>
              <h2 className="text-base font-semibold">AI 分析工具</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              <ToolCard tool={AI_ANALYSIS_TOOL} />
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
